import {
  getCoachingProgrammes,
  invalidateCoachingSurfaces,
  invalidateNowSurfaces,
  queryKeys,
} from "@healthos/client-data";
import type { api } from "@healthos/schemas";
import { type CareKind, careWords } from "@healthos/shared";
import { Switch } from "@healthos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { client, jsonOrThrow } from "../../../api/healthos";
import { useDemoConfirm } from "./demo-confirm";
import { buttonBase, PanelSection, quietButton } from "./demo-control-sections";

type Seed = api.DevSeedCoachingRequest;
type Moment = api.DevCoachingMomentRequest["moment"];

const KINDS: CareKind[] = ["coaching", "care_team"];

const ON_LABEL: Record<CareKind, string> = {
  coaching: "Me with a coach",
  care_team: "Me with a care team",
};

const ON_TITLE: Record<CareKind, string> = {
  coaching:
    "Enrol me with a coach: a session held last week, one tomorrow, a message, an action and a questionnaire",
  care_team:
    "Enrol me with an anticoagulation nurse: an evening dose, check-ins and the emergency notice",
};

const PROFESSIONAL: Array<{ kind: CareKind; label: string; title: string }> = [
  {
    kind: "coaching",
    label: "My coachees",
    title: "Make me a coach with three coachees who each need something. Needs the coach role.",
  },
  {
    kind: "care_team",
    label: "My patients",
    title:
      "Make me a clinician with three patients, one with a symptom marked urgent. Needs the coach role.",
  },
];

const MOMENTS: Array<{ moment: Moment; label: string }> = [
  { moment: "prep", label: "Prep" },
  { moment: "reminder", label: "Reminder" },
  { moment: "follow_up", label: "Notes" },
  { moment: "action", label: "Action" },
];

/** Coaching and care team demos: turn either programme on or off for me, see the professional's side, and put any moment on Today now. */
export function CoachingDemoSection(props: {
  isBusy: boolean;
  onResult: (message: string) => void;
  onError: (message: string) => void;
}) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { confirmDestructive } = useDemoConfirm();
  const programmes = useQuery({
    queryKey: queryKeys.coachingProgrammes,
    queryFn: () => getCoachingProgrammes(client()),
  });
  const on = new Set((programmes.data?.programmes ?? []).map((p) => p.kind));
  const fail = (e: unknown) => props.onError(e instanceof Error ? e.message : String(e));
  const seed = useMutation({
    mutationFn: async (input: { seed: Seed; done: string; to?: string }) => {
      await jsonOrThrow(
        await client().api.me["dev-seed-coaching"].$post({ json: input.seed }),
        "POST /api/me/dev-seed-coaching",
      );
      return input;
    },
    onSuccess: async (input) => {
      await invalidateCoachingSurfaces(qc);
      invalidateNowSurfaces(qc);
      props.onResult(input.done);
      if (input.to) navigate(input.to);
    },
    onError: fail,
  });
  const show = useMutation({
    mutationFn: async (input: { moment: Moment; kind: CareKind }) =>
      jsonOrThrow<api.DevCoachingMomentResponse>(
        await client().api.me["dev-coaching-moment"].$post({ json: input }),
        "POST /api/me/dev-coaching-moment",
      ),
    onSuccess: async ({ shown }, input) => {
      invalidateNowSurfaces(qc);
      await invalidateCoachingSurfaces(qc);
      const label = MOMENTS.find((m) => m.moment === input.moment)?.label;
      props.onResult(
        shown
          ? `${careWords(input.kind).area} ${label?.toLowerCase()} card is on Today`
          : "Nothing to show it about yet.",
      );
    },
    onError: fail,
  });
  const busy = props.isBusy || seed.isPending || show.isPending || programmes.isPending;

  const toggle = (kind: CareKind, next: boolean) => {
    const area = careWords(kind).area;
    const run = () =>
      seed.mutate({
        seed: { as: "member", kind, on: next },
        done: next ? `${area} is on` : `${area} is off`,
        to: next ? careWords(kind).path : undefined,
      });
    if (next) confirmDestructive(`Set up ${area}`, run);
    else run();
  };

  return (
    <PanelSection title="Coaching" eyebrow="Coaching and care teams">
      <div className="flex flex-col gap-1.5">
        {KINDS.map((kind) => (
          <div
            key={kind}
            className="flex items-center justify-between gap-2 text-ink-body text-xs"
            title={ON_TITLE[kind]}
          >
            <span>{ON_LABEL[kind]}</span>
            <Switch
              checked={on.has(kind)}
              disabled={busy}
              onCheckedChange={(next) => toggle(kind, next)}
              label={`${careWords(kind).area} for me`}
            />
          </div>
        ))}
      </div>
      <div className="mt-2 grid grid-cols-2 gap-1.5">
        {PROFESSIONAL.map((p) => (
          <button
            key={p.label}
            type="button"
            disabled={busy}
            title={p.title}
            onClick={() =>
              confirmDestructive(`Set up ${p.label}`, () =>
                seed.mutate({
                  seed: { as: "professional", kind: p.kind, on: true },
                  done: `${p.label} is set up`,
                  to: "/operations/coaching/coachees",
                }),
              )
            }
            className={`${buttonBase} ${quietButton}`}
          >
            {p.label}
          </button>
        ))}
      </div>
      {KINDS.filter((kind) => on.has(kind)).map((kind) => (
        <div key={kind} className="mt-2">
          <p className="mb-1 text-2xs text-ink-secondary">{careWords(kind).area} on Today now</p>
          <div className="grid grid-cols-4 gap-1.5">
            {MOMENTS.map((m) => (
              <button
                key={m.moment}
                type="button"
                disabled={busy}
                title={`Put the ${careWords(kind).area.toLowerCase()} ${m.label.toLowerCase()} card on Today and send its push`}
                onClick={() => show.mutate({ moment: m.moment, kind })}
                className={`${buttonBase} ${quietButton}`}
              >
                {show.isPending &&
                show.variables?.moment === m.moment &&
                show.variables.kind === kind
                  ? "..."
                  : m.label}
              </button>
            ))}
          </div>
        </div>
      ))}
    </PanelSection>
  );
}
